import { FC, useMemo } from 'react';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { Box, Grid } from '@mui/material';
import { getFormattedUiOptions } from '@/components/form/select/helpers';
import { PublicServiceActionOptions } from '@/lib/models/policy-model/public-services';
import FormLabel from '@/components/form-label';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

interface Props {
  policy?: PolicyModel;
  formHandle: FormHandle<PolicyModel>;
  readonly?: boolean;
}

const AutoScopeFormSection: FC<Props> = ({ formHandle, policy, readonly = false }) => {
  const { values } = formHandle;
  const { allGroups, allServices } = values;
  const serviceActionOpts = useMemo(() => getFormattedUiOptions(PublicServiceActionOptions), []);
  const { isDefault: isDefaultPolicy } = policy || { isDefault: false };

  return (
    <FormSection
      title={isDefaultPolicy ? 'Service Auto-scoping' : 'End User and Service Auto-scoping'}
      scrollId="auto-scope"
    >
      <FormSectionSubtitle>
        {isDefaultPolicy ? (
          <>
            Choose whether or not all services will be automatically scoped to this policy. This
            setting is optional, and when turned off, individual services may be scoped via
            Individual Service Scoping.
          </>
        ) : (
          <>
            Choose whether or not all end users, or all services will be automatically scoped to
            this policy. This setting is optional, and when turned off, individual groups of
            end-users and services may be scoped via Individual Group and Service Scoping.
          </>
        )}
      </FormSectionSubtitle>
      <Grid container spacing={2}>
        {!isDefaultPolicy && (
          <>
            <Grid item xs={5}>
              <FormLabel disabled={allServices}>All End Users</FormLabel>
              <InputControl
                name="allGroups"
                type="on-off-toggle-button"
                formHandle={formHandle}
                readonly={readonly}
                withNotSet={false}
                disabled={allServices}
              />
            </Grid>
            <Grid item xs={1}></Grid>
          </>
        )}
        <Grid item xs={5}>
          <Box mb={1}>
            <FormLabel disabled={allGroups}>All Services</FormLabel>
          </Box>
          <InputControl
            name="publicServices.allServicesAction"
            type="select"
            options={serviceActionOpts}
            formHandle={formHandle}
            readonly={readonly}
            size="small"
            withNotSet
            disabled={allGroups}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default AutoScopeFormSection;
